import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useDeliveryAddress } from '../context/DeliveryAddressContext';
import { API_URL } from '../config/api';
import { getFullImageUrl } from '../utils/imageUtils';
import { isCurrentlyOpen, getTodayOpeningHours } from '../utils/openingHours';
import { getStoreCategoryIcon } from '../utils/categoryIcons';
import SEO from '../components/SEO';
import './Home.css';

const CATEGORIES_VISIBLE_DEFAULT = 10;

const Stores = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language && i18n.language.startsWith('fi')) ? 'fi' : 'en';
  const { addToCart } = useCart();
  const { user } = useAuth();
  const { currentAddress, addressVersion } = useDeliveryAddress();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('Everything');
  const [categories, setCategories] = useState([]);
  const [showAllCategories, setShowAllCategories] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${API_URL}/stores/categories`);
        const list = res.data.categories || [];
        setCategories(Array.isArray(list) ? list.map((c) => {
          if (typeof c === 'string') return { value: c, fi: c, en: c };
          const v = c?.value ?? c?.fi ?? c?.en ?? '';
          return { value: v, fi: c?.fi ?? v, en: c?.en ?? v };
        }) : []);
      } catch (e) {
        console.error('Error fetching store categories:', e);
      }
    };
    fetchCategories();
  }, []);

  const fetchStores = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedCategory !== 'Everything' && selectedCategory) {
        params.category = selectedCategory;
      }
      if (currentAddress?.coordinates?.lat != null && currentAddress?.coordinates?.lng != null) {
        const lat = parseFloat(currentAddress.coordinates.lat);
        const lng = parseFloat(currentAddress.coordinates.lng);
        if (!isNaN(lat) && !isNaN(lng)) {
          params.lat = lat;
          params.lng = lng;
        }
      }
      const response = await axios.get(`${API_URL}/stores`, { params });
      setStores(response.data);
    } catch (error) {
      console.error('Error fetching stores:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, currentAddress, addressVersion]);

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  const toggleFavorite = async (storeId, e) => {
    e.stopPropagation();
    if (!user) return;
    
    try {
      const isFavorite = user.favoriteStores?.includes(storeId);
      const endpoint = `${API_URL}/users/favorite-stores/${storeId}`;
      const method = isFavorite ? 'delete' : 'post';
      await axios[method](endpoint);
      
      // Reload to get updated user
      setTimeout(() => window.location.reload(), 100);
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const isFavorite = (storeId) => {
    return user?.favoriteStores?.includes(storeId) || false;
  };

  const isStoreOpen = (store) => isCurrentlyOpen(store);

  const renderStars = (rating) => {
    if (rating == null || rating <= 0) return null;
    const stars = [];
    const fullStars = Math.min(5, Math.max(0, Math.round(rating)));
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <i key={i} className={i <= fullStars ? 'fas fa-star' : 'far fa-star'}></i>
      );
    }
    return <span className="rating-stars">{stars}</span>;
  };

  if (loading) {
    return <div className="home-container"><div className="container">Loading...</div></div>;
  }

  return (
    <div className="home-container">
      <SEO
        title="Kaupat"
        description="Tilaa päivittäistavarat ja muut tuotteet kaupasta kotiin. Valitse kauppa ja selaa valikoimaa – toimitus ovellesi."
        path="/stores"
      />
      <div className="container">
        <h1>{t('nav.stores')}</h1>
        <div className="categories categories-with-icons">
          <button
            key="Everything"
            type="button"
            className={`category-btn category-btn-with-icon ${selectedCategory === 'Everything' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('Everything')}
          >
            <i className={getStoreCategoryIcon('Everything')} aria-hidden="true" />
            <span>{t('home.categories.everything')}</span>
          </button>
          {(showAllCategories ? categories : categories.slice(0, CATEGORIES_VISIBLE_DEFAULT)).map((cat) => {
            const value = (cat && (cat.value ?? cat.fi ?? cat.en)) || '';
            const displayLabel = (cat && (cat[lang] || cat.fi || cat.en)) || value;
            return (
              <button
                key={value}
                type="button"
                className={`category-btn category-btn-with-icon ${selectedCategory === value ? 'active' : ''}`}
                onClick={() => setSelectedCategory(value)}
              >
                <i className={getStoreCategoryIcon(value)} aria-hidden="true" />
                <span>{displayLabel}</span>
              </button>
            );
          })}
          {categories.length > CATEGORIES_VISIBLE_DEFAULT && (
            <button
              type="button"
              className="category-btn category-btn-more"
              onClick={() => setShowAllCategories(!showAllCategories)}
              aria-expanded={showAllCategories}
              aria-label={showAllCategories ? (t('home.showLessCategories') || 'Show less categories') : (t('home.showMoreCategories') || 'Show more categories')}
            >
              <i className={showAllCategories ? 'fas fa-chevron-up' : 'fas fa-chevron-down'} aria-hidden="true" />
              <span>{showAllCategories ? (t('home.showLess') || 'Less') : (t('home.showMore') || 'More')}</span>
            </button>
          )}
        </div>
        {stores.length === 0 ? (
          <div className="no-restaurants">{t('stores.noStores') || 'No stores found'}</div>
        ) : (
          <div className="restaurants-grid">
            {stores.map((store) => (
              <Link key={store._id} to={`/store/${store._id}`} className="restaurant-card">
                <div className="restaurant-header">
                  {store.image && (
                    <div className="restaurant-image-container">
                      <img src={getFullImageUrl(store.image)} alt={store.name} className="restaurant-image" loading="lazy" decoding="async" />
                      {store.openingHours && !isStoreOpen(store) && (
                        <div className="restaurant-status-overlay">
                          {t('restaurant.closed')}
                        </div>
                      )}
                      {/* Show "not delivering" overlay if store is outside delivery range */}
                      {currentAddress?.coordinates?.lat && 
                       currentAddress?.coordinates?.lng &&
                       store.distance !== undefined && 
                       store.distance !== null &&
                       !isNaN(store.distance) && (
                        (() => {
                          const maxDist = store.maxDeliveryDistance ?? 20; // Default 20km if not set
                          if (store.distance > maxDist) {
                            return (
                              <div className="restaurant-delivery-overlay">
                                <span>{t('restaurant.outsideDeliveryArea')}</span>
                              </div>
                            );
                          }
                          return null;
                        })()
                      )}
                    </div>
                  )}
                  {user && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleFavorite(store._id, e);
                      }}
                      className={`favorite-btn ${isFavorite(store._id) ? 'active' : ''}`}
                    >
                      <i className={`fas fa-heart ${isFavorite(store._id) ? 'active' : ''}`}></i>
                    </button>
                  )}
                </div>
                <div className="restaurant-info">
                  <div className="restaurant-title-row">
                    <h3>{store.name}</h3>
                    {(store.settings?.displayStarRatings !== false && store.rating != null && store.rating > 0) ? renderStars(store.rating) : null}
                  </div>
                  <p className="restaurant-description">{store.description}</p>
                  <div className="pricing-range">{store.pricingRange || '€€'}</div>
                  <div className="restaurant-details">
                    <span>{t('restaurant.deliveryTime')}: {store.deliveryTime || '2-3 days'}</span>
                    <span>{t('restaurant.deliveryFee')}: €{store.deliveryFee || '0'}</span>
                    {store.distance && (
                      <span>{store.distance} {t('restaurant.distance') || 'km'}</span>
                    )}
                    {(() => {
                      const today = getTodayOpeningHours(store);
                      if (today) return <span>{today.openingTime} – {today.closingTime}</span>;
                      if (store?.openingHours) return <span>{t('restaurant.closedToday') || 'Closed today'}</span>;
                      return null;
                    })()}
                  </div>
                  {store.deliveryMethods && (
                    <div className="delivery-methods">
                      {store.deliveryMethods.delivery && (
                        <span className="delivery-badge">{t('restaurant.delivery')}</span>
                      )}
                      {store.deliveryMethods.pickup && (
                        <span className="delivery-badge">{t('restaurant.pickup')}</span>
                      )}
                      {store.deliveryMethods.eatIn && (
                        <span className="delivery-badge">{t('restaurant.eatIn')}</span>
                      )}
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Stores;
